import { ApiComms } from './comms';
import {
  StripePaymentRecord,
  BulkPaymentJob,
  BulkUploadResponse,
  BulkConfirmResult,
} from '../../types';
import { apiFetch } from './base';

export class ApiPayments extends ApiComms {
  // ============ STRIPE PAYMENT INTENTS ============

  async createPaymentIntent(payload: {
    condoId: string;
    ownerId: string;
    feeId?: string;
    amount: number; // in cents
    currency?: string;
    idempotencyKey: string;
  }): Promise<{ clientSecret: string }> {
    // Backend expects amountCents (not amount)
    const { amount, ownerId, feeId, ...rest } = payload;
    return this.request('/payment-intents/create', {
      method: 'POST',
      body: JSON.stringify({ ...rest, amountCents: amount }),
    });
  }

  async createStripeCustomer(payload: {
    condoId: string;
    ownerId: string;
    email: string;
    name: string;
  }): Promise<{ customerId: string }> {
    return this.request('/payment-intents/create-customer', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getStripePaymentRecords(condoId: string): Promise<StripePaymentRecord[]> {
    return this.request(`/payment-intents/records/${condoId}`);
  }

  // ============ BULK PAYMENT UPLOAD ============

  async uploadBulkPayments(condoId: string, file: File): Promise<BulkUploadResponse> {
    // Sprint 20 — see uploadDocument note. apiFetch picks up the timeout,
    // X-Request-Id, and 401 → silent-refresh-and-retry path.
    const formData = new FormData();
    formData.append('condoId', condoId);
    formData.append('file', file);
    const response = await apiFetch('/payments/bulk/upload', {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async confirmBulkPayments(jobId: string, validRows: any[]): Promise<BulkConfirmResult> {
    return this.request(`/payments/bulk/confirm/${jobId}`, {
      method: 'POST',
      body: JSON.stringify({ validRows }),
    });
  }

  async getBulkPaymentJobs(condoId: string): Promise<BulkPaymentJob[]> {
    return this.request(`/payments/bulk/jobs/${condoId}`);
  }
  // ============ SPRINT 41 — TAX + AUTO-MATCH ============

  /** Province list with active rates (typically 1 or 2 rows per province). */
  async listTaxRatesForProvince(province: string): Promise<any[]> {
    return this.request(`/tax-rates/province/${encodeURIComponent(province)}`);
  }

  async listAllTaxRates(): Promise<any[]> {
    return this.request('/tax-rates');
  }

  /** Run the auto-matcher on a parsed CSV; returns green/yellow/red counts + per-row matches. */
  async runBulkPaymentMatch(jobId: string, condoId: string, rows: Array<{
    index: number; unitNumber: string; amount: number;
  }>): Promise<{ green: number; yellow: number; red: number; rows: any[] }> {
    return this.request(`/payments/bulk/match/${jobId}`, {
      method: 'POST',
      body: JSON.stringify({ condoId, rows }),
    });
  }

  async decideBulkMatch(matchId: string, feeId: string): Promise<any> {
    return this.request(`/payments/bulk/match/${matchId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ feeId }),
    });
  }

  async listBulkMatches(jobId: string): Promise<any[]> {
    return this.request(`/payments/bulk/match/${jobId}`);
  }
  // ============ SPRINT 40 — REFUNDS / ACH / AUTO-PAY ============

  /** Issue a refund against a Stripe payment record. Admin / manager only. */
  async issueRefund(payload: {
    stripeRecordId: string;
    amountCents:    number;
    reason:         string;
  }): Promise<any> {
    return this.request('/payment-intents/refund', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listRefundsForCondo(condoId: string): Promise<any[]> {
    return this.request(`/payment-intents/refunds/${condoId}`);
  }

  /** Create an ACH (Canadian ACSS PAD) PaymentIntent. */
  async createAchPaymentIntent(payload: {
    condoId:          string;
    unitNumber?:      string;
    amountCents:      number;
    currency?:        string;
    stripeCustomerId?: string;
    feeDescription?:  string;
    feePeriod?:       string;
  }): Promise<{ clientSecret: string; paymentIntentId: string }> {
    return this.request('/payment-intents/ach', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Enroll the current owner in auto-pay. */
  async enrollAutoPay(payload: {
    condoId:                string;
    ownerId:                string;
    stripeCustomerId:       string;
    stripePaymentMethodId:  string;
    paymentMethodType?:     'card' | 'us_bank_account' | 'acss_debit';
    enabledFeeTypes:        string[];     // JSON-encoded server-side
  }): Promise<any> {
    return this.request('/autopay/enroll', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        enabledFeeTypes: JSON.stringify(payload.enabledFeeTypes),
      }),
    });
  }

  async pauseAutoPay(enrollmentId: string)  { return this.request(`/autopay/${enrollmentId}/pause`,  { method: 'POST' }); }
  async resumeAutoPay(enrollmentId: string) { return this.request(`/autopay/${enrollmentId}/resume`, { method: 'POST' }); }
  async cancelAutoPay(enrollmentId: string) { return this.request(`/autopay/${enrollmentId}/cancel`, { method: 'POST' }); }

  async listAutoPayForCondo(condoId: string): Promise<any[]> {
    return this.request(`/autopay/condo/${condoId}`);
  }

  async autoPayRecentRuns(enrollmentId: string): Promise<any[]> {
    return this.request(`/autopay/${enrollmentId}/runs`);
  }
}
